<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import {computed, ref, watch} from "vue";
import '@vuepic/vue-datepicker/dist/main.css'
import Datepicker from "@vuepic/vue-datepicker";
import dayjs from "dayjs";
import {useHelpers, useTenantTextRules} from "@/helpers";

const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const props = defineProps({
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  },
  isGarmentColorPricingView: {
    type: Boolean,
    default: false,
  },
});
const currentUser = usePage().props.value.auth.user;
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
watch(projectProperty, (value) => {
  resetForm(value);
  project = projectProperty.value;
});
const dueDate = ref(project?.purchase_order?.purchaseduedate ? (project?.purchase_order?.purchaseduedate * 1000) : null);
const dateDisplayFormat = (date) => {
  const options = {year: 'numeric', month: 'long', day: 'numeric'};

  return date.toLocaleDateString('en-US', options);
}

const minDate = dayjs().toDate();

const form = useForm({
  _method: 'POST',
  purchase_order: {
    purchaseorder: project?.purchase_order?.purchaseorder,
    purchaseduedate: dueDate.value,
    qtypeople: parseInt(project?.purchase_order?.qtypeople || 0),
    sizeneeded: project?.purchase_order?.sizeneeded,
    garmentdesc: project?.purchase_order?.garmentdesc,
    pt_invoice: project?.purchase_order?.pt_invoice,
  }
});

const generatePoForm = useForm({});

const saving = ref(false);
const saveForm = () => {
  saving.value = true;
  form._method = 'PUT';
  form.purchase_order.purchaseduedate = dueDate.value;
  if (dueDate.value !== null && String(dueDate.value).length > 11) { // Fix the conversion of the timestamp between PHP seconds and JS milliseconds
    form.purchase_order.purchaseduedate = form.purchase_order.purchaseduedate / 1000;
  }
  form.post(route('admin.projects.update', {project: project.pid}), {
    preserveScroll: true,
    onFinish() {
      saving.value = false;
    }
  });
  saving.value = false;
};

const resetForm = (projectData) => {
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }

  Object.keys(form.data().purchase_order).forEach(key => {
    if (key !== 'purchaseduedate') {
      form.purchase_order[key] = projectData.purchase_order[key];
    }
  });
  dueDate.value = projectData.purchase_order.purchaseduedate ? (projectData.purchase_order.purchaseduedate * 1000) : null
}

const generatePoSaving = ref(false);
const generatePO = () => {
  generatePoSaving.value = true;
  generatePoForm.post(route('admin.projects.purchase-order.generate', {project: project.pid}), {
    preserveScroll: true,
    onFinish() {
      generatePoSaving.value = false;
    }
  });
}

const defaultGarmentTemplate = [
  'Position: ',
  'Style: ',
  'Description: ',
  'Fabric: ',
  'Sizes: ',
  'Total: ',
].join('\n');

const defaultGarmentColorGarmentTemplate = [
  'Check out/In Person: ',
  'Equipment Make: ',
  'Equipment Model: ',
  'Hours: ',
  'Fuel: ',
  'Damage: ( ) Yes ( ) No',
  'Notes: ',
  '',
  'Date Needed on Job site:',
  'Job Site Address:',
  'Job Site Contact Name:',
  'Job Site Contact Phone:',
].join('\n');

const purchaseInsertTemplateButtonLabel = computed(() =>
  resolveTextRule(
    'project.purchase.insert_template_button',
    'Insert Garment Template',
    'project.purchase.insert_template_button'
  )
);

const purchaseGarmentTemplate = computed(() =>
  resolveTextRule(
    'project.purchase.garment_template',
    props.isGarmentColorPricingView ? defaultGarmentColorGarmentTemplate : defaultGarmentTemplate,
    'project.purchase.garment_template'
  )
);

const insertGarmentTemplate = () => {
  const current = form.purchase_order.garmentdesc || '';
  const needsSeparator = current.length > 0 && !current.endsWith('\n\n');
  form.purchase_order.garmentdesc = (needsSeparator ? current + '\n\n' : current) + purchaseGarmentTemplate.value + '\n\n';
}

const purchaseOrderLabel = computed(() =>
  resolveTextRule(
    'project.purchase.label',
    'Purchase Order',
    'project.purchase.label'
  )
);

const purchaseDueDateLabel = computed(() =>
  resolveTextRule(
    'project.purchase.due_date',
    'Purchase Order Due Date',
    'project.purchase.due_date'
  )
);

const purchaseQuantityLabel = computed(() =>
  resolveTextRule(
    'project.purchase.quantity_of_garments',
    'Quantity of Garments',
    'project.purchase.quantity_of_garments'
  )
);

const purchaseSizesNeededLabel = computed(() =>
  resolveTextRule(
    'project.purchase.sizes_needed',
    'Sizes Needed',
    'project.purchase.sizes_needed'
  )
);

const purchaseGarmentDescriptionLabel = computed(() =>
  resolveTextRule(
    'project.purchase.garment_description',
    'Garment Description',
    'project.purchase.garment_description'
  )
);
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md">
    <div class="sm:grid grid-cols-6 gap-6">
      <!-- Purchase Order -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="purchase-order" :value="purchaseOrderLabel"/>
        <TextInput
            id="purchase-order"
            type="text"
            class="mt-1 inline"
            v-model="form.purchase_order.purchaseorder"
            :disabled="(!helpers.userIsAdmin(currentUser) && project.purchase_order.purchaseorder) || (!helpers.userCanManageProject(currentUser))"
        />
        <PrimaryButton type="button" class="ml-2 md:mt-2 mt-0"
                       v-if="(!project.purchase_order.purchaseorder && helpers.userCanManageProject(currentUser))"
                       @click.prevent="generatePO()" :disabled="generatePoSaving">Generate P.O.
        </PrimaryButton>
        <InputError class="mt-2" :message="form.errors['purchase_order.purchaseorder']"/>
      </div>
      <!-- Due Date -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="due-date" :value="purchaseDueDateLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="dueDate" :format="dateDisplayFormat"
                    :min-date="minDate"
                    model-type="timestamp" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"
                    :disabled="(!helpers.userCanManageProject(currentUser))"/>
        <InputError class="mt-2" :message="form.errors['purchase_order.purchaseduedate']"/>
      </div>
      <!-- Quantity of People -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="quantity-of-people" :value="purchaseQuantityLabel"/>
        <TextInput
            id="quantity-of-people"
            type="number"
            class="mt-1 block w-full"
            v-model="form.purchase_order.qtypeople"
            :disabled="(!helpers.userCanManageProject(currentUser))"
        />
        <InputError class="mt-2" :message="form.errors['purchase_order.qtypeople']"/>
      </div>
      <!-- Sizes Needed -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="sizes-needed" :value="purchaseSizesNeededLabel"/>
        <TextInput
            id="sizes-needed"
            type="text"
            class="mt-1 block w-full"
            v-model="form.purchase_order.sizeneeded"
            :disabled="(!helpers.userCanManageProject(currentUser))"
        />
        <InputError class="mt-2" :message="form.errors['purchase_order.sizeneeded']"/>
      </div>
      <!-- Garment Description -->
      <div class="col-span-6 sm:col-span-6">
        <InputLabel for="garment-description" :value="purchaseGarmentDescriptionLabel"/>
        <div class="mt-1 mb-2">
          <PrimaryButton type="button"
                         @click.prevent="insertGarmentTemplate"
                         v-if="helpers.userCanManageProject(currentUser)">
            {{ purchaseInsertTemplateButtonLabel }}
          </PrimaryButton>
        </div>
        <textarea
            rows="10"
            id="garment-description"
            v-model="form.purchase_order.garmentdesc"
            :disabled="(!helpers.userCanManageProject(currentUser))"
            class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
        ></textarea>
        <InputError class="mt-2" :message="form.errors['purchase_order.garmentdesc']"/>
      </div>
      <!-- PT Invoice# -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="pt-invoice" value="PT Invoice#"/>
        <TextInput
            id="pt-invoice"
            type="text"
            class="mt-1 block w-full"
            v-model="form.purchase_order.pt_invoice"
            :disabled="(!helpers.userCanManageProject(currentUser))"
        />
        <InputError class="mt-2" :message="form.errors['purchase_order.pt_invoice']"/>
      </div>
    </div>
  </div>
  <div
    class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton @click.prevent="saveForm()" v-if="hideSave === false">Save</PrimaryButton>
  </div>
</template>
